import React, { useCallback, useEffect, useState } from 'react';

import {
  EsteiraParalela,
  EsteirasParalelasDoVeiculo,
  encerrarEsteiraParalela,
  listarEsteirasParalelas,
} from '../../api/esteirasParalelasApi';
import { notify } from '../../shared/feedback/notify';
import '../../shared/feedback/erro-de-tela.css';
import '../../shared/ui/badge-status.css';
import './esteiras-paralelas.css';

interface EsteirasParalelasProps {
  carId: string;
  /** Só o Chefe encerra. O TI enxerga o bloco em leitura (ADR 0012). */
  podeEncerrar?: boolean;
  /** Recarrega a tela hospedeira depois de encerrar (a listagem tem o selo da placa). */
  onEncerrada?: () => void;
  /** Força uma recarga do bloco — usado depois de abrir uma paralela no mesmo modal. */
  recarregar?: number;
}

const MOTIVO_ENCERRAMENTO: Record<string, string> = {
  CONCLUIDA: 'serviço concluído',
  VIROU_PRINCIPAL: 'passou a ser a etapa do veículo',
  ENCERRADA_PELO_CHEFE: 'encerrada pelo Chefe',
  SAIDA_PARA_SISTEMA: 'o veículo saiu para uma etapa de fluxo livre',
  LIBERACAO_REMOVIDA: 'a liberação da esteira foi removida',
};

/**
 * Bloco "Esteiras em paralelo" (at-99, ADR 0050) — compartilhado pelos modais de placa.
 *
 * Mostra quais esteiras têm serviço em curso além da etapa do veículo, desde quando, quem abriu,
 * o motivo (quando informado) e **quem está trabalhando em cada uma**. Importar, nunca recopiar
 * (molde do bloco de sub-tarefas da at-89).
 *
 * 🔴 **Falha de carga NUNCA vira "não há esteira em paralelo".** O early return de erro vem
 * ANTES do de lista vazia, e há sentinela provado por mutação: com o erro engolido, o Chefe
 * concluiria que ninguém está trabalhando em paralelo e abriria a mesma esteira de novo (409),
 * ou pior, moveria o veículo achando que não há nada em curso (regra da at-60).
 *
 * ⚠️ As encerradas recentes aparecem de propósito: é o que explica hora já apontada numa esteira
 * pela qual o veículo nunca passou.
 */
const EsteirasParalelas: React.FC<EsteirasParalelasProps> = ({
  carId,
  podeEncerrar = false,
  onEncerrada,
  recarregar = 0,
}) => {
  const [dados, setDados] = useState<EsteirasParalelasDoVeiculo | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [encerrando, setEncerrando] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      setDados(await listarEsteirasParalelas(carId));
      setErro(false);
    } catch (error) {
      setDados(null);
      setErro(true);
      notify.errorFrom(
        error,
        'Não foi possível carregar as esteiras abertas em paralelo deste veículo. Recarregue a página; se persistir, acione a TI.',
      );
    } finally {
      setCarregando(false);
    }
  }, [carId]);

  useEffect(() => {
    void carregar();
  }, [carregar, recarregar]);

  const handleEncerrar = async (paralela: EsteiraParalela, confirmar = false) => {
    setEncerrando(paralela.id);
    try {
      const resultado = await encerrarEsteiraParalela(carId, paralela.id, confirmar);
      notify.success(
        resultado.sessoesEncerradas > 0
          ? `${paralela.nome} foi encerrada e ${resultado.sessoesEncerradas === 1 ? 'a atividade em curso foi encerrada' : `${resultado.sessoesEncerradas} atividades em curso foram encerradas`}. As horas seguem para a sua aprovação.`
          : `${paralela.nome} não está mais aberta em paralelo neste veículo.`,
      );
      await carregar();
      onEncerrada?.();
    } catch (error: any) {
      const corpo = error?.response?.data;
      if (corpo?.code === 'PARALELA_COM_TRABALHO' && !confirmar) {
        // O 409 traz quem seria interrompido e NÃO gravou nada — a confirmação descreve o
        // efeito antes de executar (padrão dos ADR 0019/0029/0043).
        // eslint-disable-next-line no-restricted-globals
        const aceitou = window.confirm(
          `${corpo.message}\n\nDeseja encerrar ${paralela.nome} mesmo assim?`,
        );
        if (aceitou) {
          await handleEncerrar(paralela, true);
          return;
        }
      } else {
        // Texto FIXO, não `actionError`: o que importa ao Chefe é a consequência — a esteira
        // continua aberta —, não o motivo HTTP (regra da at-65).
        notify.error(
          `Não foi possível encerrar ${paralela.nome}. A esteira continua aberta em paralelo neste veículo.`,
        );
      }
    } finally {
      setEncerrando(null);
    }
  };

  // 🔴 A ORDEM destes três returns é a regra: erro ANTES de vazio. Invertida, falha de carga
  // fica indistinguível de "não há serviço em paralelo".
  if (carregando && !dados) {
    return <p className="esteiras-paralelas-carregando">Carregando esteiras em paralelo...</p>;
  }

  if (erro) {
    return (
      <p className="erro-de-tela">
        Não foi possível verificar se há esteira aberta em paralelo neste veículo. Pode haver
        serviço em curso que não está sendo exibido.
      </p>
    );
  }

  const vigentes = dados?.vigentes ?? [];
  const encerradas = dados?.encerradas ?? [];
  if (vigentes.length === 0 && encerradas.length === 0) return null;

  return (
    <div className="esteiras-paralelas">
      {vigentes.length > 0 && (
        <>
          <p className="esteiras-paralelas-hint">
            Além de <strong>{dados?.principal.nome}</strong>, que é a etapa do veículo, há serviço
            em curso nas esteiras abaixo. O veículo permanece na etapa: o prazo dela não reinicia e
            o quadro não muda de coluna.
          </p>
          <ul className="esteiras-paralelas-lista">
            {vigentes.map((paralela) => (
              <li key={paralela.id} className="esteiras-paralelas-item">
                <div className="esteiras-paralelas-linha">
                  <span className="cilia-badge badge-azul">{paralela.nome}</span>
                  <span className="esteiras-paralelas-meta">
                    aberta em {new Date(paralela.abertaEm).toLocaleDateString('pt-BR')}
                    {paralela.abertaPorNome ? ` por ${paralela.abertaPorNome}` : ''}
                  </span>
                  {podeEncerrar && (
                    <button
                      type="button"
                      className="esteiras-paralelas-encerrar"
                      onClick={() => handleEncerrar(paralela)}
                      disabled={encerrando === paralela.id}
                    >
                      {encerrando === paralela.id ? 'Encerrando...' : 'Encerrar'}
                    </button>
                  )}
                </div>
                {paralela.motivo && (
                  <p className="esteiras-paralelas-motivo">Motivo: {paralela.motivo}</p>
                )}
                {paralela.trabalhando && paralela.trabalhando.length > 0 && (
                  <p className="esteiras-paralelas-trabalhando">
                    Trabalhando agora:{' '}
                    {paralela.trabalhando
                      .map((t) => t.collaboratorName ?? 'colaborador')
                      .join(', ')}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </>
      )}

      {encerradas.length > 0 && (
        <div className="esteiras-paralelas-encerradas">
          <p className="esteiras-paralelas-hint">
            Encerradas recentemente — aparecem aqui porque explicam horas já apontadas em esteiras
            pelas quais o veículo não passou como etapa.
          </p>
          <ul className="esteiras-paralelas-lista">
            {encerradas.map((paralela) => (
              <li key={paralela.id} className="esteiras-paralelas-item esteiras-paralelas-item-off">
                <span className="cilia-badge badge-cinza">{paralela.nome}</span>
                <span className="esteiras-paralelas-meta">
                  {paralela.encerradaEm
                    ? `encerrada em ${new Date(paralela.encerradaEm).toLocaleDateString('pt-BR')}`
                    : 'encerrada'}
                  {paralela.encerradaMotivo
                    ? ` — ${MOTIVO_ENCERRAMENTO[paralela.encerradaMotivo] ?? paralela.encerradaMotivo}`
                    : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default EsteirasParalelas;
